import { useState, useMemo } from "react";
import { useApp } from "../context/AppContext";

const AH_BASE = "https://author.house";

// Екранування тексту, що вставляється у HTML/атрибути.
const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// Транслітерація uk/ru→latin для slug — дзеркало server/routes/authors.js.
const TRANSLIT = { а:"a",б:"b",в:"v",г:"h",ґ:"g",д:"d",е:"e",є:"ie",ё:"e",ж:"zh",з:"z",и:"y",і:"i",ї:"i",й:"i",к:"k",л:"l",м:"m",н:"n",о:"o",п:"p",р:"r",с:"s",т:"t",у:"u",ф:"f",х:"kh",ц:"ts",ч:"ch",ш:"sh",щ:"shch",ъ:"",ы:"y",ь:"",э:"e",ю:"iu",я:"ia" };
const slugify = (s) => String(s || "").toLowerCase().trim()
  .split("").map((ch) => TRANSLIT[ch] != null ? TRANSLIT[ch] : ch).join("")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 70);

const hostLabel = (u) => {
  try { const h = new URL(u).hostname.replace(/^www\./, ""); return h.split(".")[0]; } catch { return "link"; }
};

// ─── Побудова готової верстки сторінки автора (self-contained) ───
function buildPage(f, opt) {
  const acc = opt.accent || "#6366f1";
  const dark = opt.theme === "dark";
  const bg = dark ? "#0f1221" : "#ffffff";
  const fg = dark ? "#e8e9f3" : "#1a1a2e";
  const mut = dark ? "#9aa0b4" : "#6b7280";
  const line = dark ? "rgba(255,255,255,.1)" : "#ececf3";
  const soft = dark ? "rgba(255,255,255,.04)" : "#f7f7fb";
  const radius = opt.avatar === "square" ? "16px" : "50%";
  const centered = opt.layout === "center";

  const topics = (f.knowsAbout || "").split(",").map((s) => s.trim()).filter(Boolean);
  const creds = (f.credentials || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const bioParas = (f.bio || "").split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);
  const socials = (f.socials || []).filter((s) => s.url);

  const topicsHtml = (opt.showTopics && topics.length)
    ? `<div class="spa-topics">${topics.map((tp) => `<span class="spa-chip">${esc(tp)}</span>`).join("")}</div>` : "";

  const credsHtml = (opt.showCreds && (creds.length || f.education))
    ? `<div class="spa-block"><h3 class="spa-h3">Кваліфікація та досвід</h3><ul class="spa-list">${
        (f.education ? `<li>${esc(f.education)}</li>` : "") + creds.map((c) => `<li>${esc(c)}</li>`).join("")
      }</ul></div>` : "";

  const bioHtml = bioParas.length
    ? `<div class="spa-bio">${bioParas.map((p) => `<p>${esc(p)}</p>`).join("")}</div>` : "";

  const socialsHtml = (opt.showSocials && socials.length)
    ? `<div class="spa-socials">${socials.map((s) =>
        `<a href="${esc(s.url)}" target="_blank" rel="noopener nofollow" class="spa-social">${esc(s.label || hostLabel(s.url))}</a>`
      ).join("")}</div>` : "";

  const contactHtml = (opt.showContact && f.email)
    ? `<div class="spa-contact"><a href="mailto:${esc(f.email)}">${esc(f.email)}</a></div>` : "";

  const roleLine = [f.jobTitle, f.experience].filter(Boolean).map(esc).join(" · ");

  const avatarHtml = f.photo
    ? `<img class="spa-avatar" src="${esc(f.photo)}" alt="${esc(f.name)}" loading="lazy">`
    : `<div class="spa-avatar spa-avatar--ph">${esc((f.name || "A").trim().charAt(0).toUpperCase())}</div>`;

  const css = `
.spa{--acc:${acc};font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  max-width:860px;margin:0 auto;color:${fg};background:${bg};border:1px solid ${line};
  border-radius:20px;padding:32px;box-sizing:border-box;line-height:1.6}
.spa *{box-sizing:border-box}
.spa-head{display:flex;gap:24px;align-items:center;${centered ? "flex-direction:column;text-align:center;" : ""}}
.spa-avatar{width:120px;height:120px;border-radius:${radius};object-fit:cover;flex-shrink:0;
  border:3px solid var(--acc)}
.spa-avatar--ph{display:flex;align-items:center;justify-content:center;font-size:48px;font-weight:800;
  color:#fff;background:var(--acc)}
.spa-name{font-size:28px;font-weight:800;margin:0 0 4px}
.spa-role{display:inline-block;font-size:14px;font-weight:600;color:#fff;background:var(--acc);
  padding:4px 12px;border-radius:999px;margin-bottom:8px}
.spa-socials{display:flex;gap:8px;flex-wrap:wrap;${centered ? "justify-content:center;" : ""}margin-top:6px}
.spa-social{font-size:13px;font-weight:600;text-decoration:none;color:var(--acc);
  border:1px solid var(--acc);border-radius:8px;padding:3px 10px}
.spa-desc{font-size:16px;color:${fg};margin:20px 0 0;font-weight:500}
.spa-bio{margin-top:14px;color:${mut};font-size:15px}
.spa-bio p{margin:0 0 10px}
.spa-block{margin-top:20px;background:${soft};border:1px solid ${line};border-radius:14px;padding:16px 18px}
.spa-h3{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--acc);margin:0 0 10px}
.spa-list{margin:0;padding-left:18px;color:${fg};font-size:14px}
.spa-list li{margin-bottom:6px}
.spa-topics{display:flex;gap:7px;flex-wrap:wrap;margin-top:18px;${centered ? "justify-content:center;" : ""}}
.spa-chip{font-size:12px;font-weight:600;color:var(--acc);background:${dark ? "rgba(99,102,241,.14)" : "rgba(99,102,241,.08)"};
  border-radius:8px;padding:4px 11px}
.spa-contact{margin-top:18px;font-size:14px}
.spa-contact a{color:var(--acc);text-decoration:none;font-weight:600}
@media(max-width:560px){.spa-head{flex-direction:column;text-align:center}.spa{padding:22px}}
`.trim();

  const jsonld = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    ...(f.pageUrl ? { "@id": f.pageUrl + "#profilepage", url: f.pageUrl } : {}),
    mainEntity: {
      "@type": "Person",
      ...(f.pageUrl ? { "@id": f.pageUrl + "#person", url: f.pageUrl } : {}),
      name: f.name || "",
      ...(f.jobTitle ? { jobTitle: f.jobTitle } : {}),
      ...(f.photo ? { image: f.photo } : {}),
      ...(f.description ? { description: f.description } : {}),
      ...(f.worksFor ? { worksFor: { "@type": "Organization", name: f.worksFor } } : {}),
      ...(f.education ? { alumniOf: { "@type": "EducationalOrganization", name: f.education } } : {}),
      ...(creds.length ? { hasCredential: creds.map((c) => ({ "@type": "EducationalOccupationalCredential", credentialCategory: c })) } : {}),
      ...(topics.length ? { knowsAbout: topics } : {}),
      ...(socials.length ? { sameAs: socials.map((s) => s.url) } : {}),
    },
  };

  const html = `<!-- Сторінка автора · згенеровано Spalah -->
<section class="spa">
  <style>
${css}
  </style>
  <div class="spa-head">
    ${avatarHtml}
    <div class="spa-id">
      <h1 class="spa-name">${esc(f.name) || "Ім'я автора"}</h1>
      ${roleLine ? `<div class="spa-role">${roleLine}</div>` : ""}
      ${socialsHtml}
    </div>
  </div>
  ${f.description ? `<p class="spa-desc">${esc(f.description)}</p>` : ""}
  ${bioHtml}
  ${credsHtml}
  ${topicsHtml}
  ${contactHtml}
</section>
<script type="application/ld+json">
${JSON.stringify(jsonld, null, 2)}
</script>`;

  return { html, bg };
}

export default function AuthorPageGenerator({ onClose }) {
  const { t, dark, proj, flash, apiFetch } = useApp();
  const [f, setF] = useState({
    name: "", jobTitle: "", experience: "", photo: "", pageUrl: "", slug: "",
    description: "", bio: "", education: "", credentials: "", knowsAbout: "",
    worksFor: proj?.name || "", email: "",
    socials: [{ url: "" }],
  });
  const [publishing, setPublishing] = useState(false);
  const [pubUrl, setPubUrl] = useState("");
  const [opt, setOpt] = useState({
    accent: "#6366f1", theme: "light", avatar: "circle", layout: "left",
    showTopics: true, showCreds: true, showSocials: true, showContact: true,
  });
  const [tab, setTab] = useState("preview"); // preview | code
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setO = (k, v) => setOpt((p) => ({ ...p, [k]: v }));
  const setSocial = (i, v) => setF((p) => ({ ...p, socials: p.socials.map((s, j) => j === i ? { url: v } : s) }));
  const addSocial = () => setF((p) => ({ ...p, socials: [...p.socials, { url: "" }] }));
  const rmSocial = (i) => setF((p) => ({ ...p, socials: p.socials.filter((_, j) => j !== i) }));

  // Живий slug+URL профілю на author.house (формується одразу з імені; slug можна змінити).
  const autoSlug = slugify(f.name);
  const effSlug = (f.slug && f.slug.trim()) ? slugify(f.slug) : autoSlug;
  const profileUrl = effSlug ? `${AH_BASE}/${effSlug}` : "";

  // Розмітка/верстка завжди використовують канонічний author.house-URL як @id.
  const { html, bg } = useMemo(() => buildPage({ ...f, pageUrl: profileUrl }, opt), [f, opt, profileUrl]);
  const previewDoc = `<!doctype html><html lang="uk"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0;padding:28px;background:${dark ? "#0b0d17" : "#eef0f6"}">${html}</body></html>`;

  const copy = () => {
    try { navigator.clipboard.writeText(html); setCopied(true); setTimeout(() => setCopied(false), 1600); flash && flash("Код скопійовано"); } catch {}
  };

  // Зберегти → дані синкаються в author.house, повертається публічний URL (додається в sameAs).
  const publish = async () => {
    if (!f.name.trim()) { flash && flash("Вкажіть ім'я автора"); return; }
    setPublishing(true);
    try {
      const r = await apiFetch(`/api/authors/publish`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: f.name, jobTitle: f.jobTitle, experience: f.experience, photo: f.photo,
          description: f.description, bio: f.bio, education: f.education,
          credentials: f.credentials, knowsAbout: f.knowsAbout, worksFor: f.worksFor,
          email: f.email, sameAs: (f.socials || []).map((s) => s.url).filter(Boolean),
          slug: effSlug, opt,
        }),
      }).then((rr) => rr.json());
      if (!r || !r.url) { flash && flash("Помилка публікації: " + (r?.error || "")); setPublishing(false); return; }
      setPubUrl(r.url);
      // авто: профіль author.house → у sameAs + як @id сторінки, і зафіксувати slug
      setF((p) => {
        const socials = (p.socials || []).filter((s) => s.url);
        const has = socials.some((s) => s.url === r.url);
        return {
          ...p,
          slug: r.slug,
          pageUrl: p.pageUrl || r.url,
          socials: has ? p.socials : [...socials, { url: r.url }],
        };
      });
      flash && flash("Опубліковано ✓");
    } catch (e) { flash && flash("Помилка: " + e.message); }
    setPublishing(false);
  };

  const T = t || {};
  const inp = { width: "100%", background: T.bg || "#fafafa", color: T.text, border: `1px solid ${T.border || "#e5e7eb"}`, borderRadius: 8, padding: "8px 10px", fontFamily: "inherit", fontSize: 13, boxSizing: "border-box" };
  const lbl = { fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 4 };
  const field = (label, k, ph, area) => (
    <div style={{ marginBottom: 10 }}>
      <label style={lbl}>{label}</label>
      {area
        ? <textarea rows={area} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} style={{ ...inp, resize: "vertical" }} />
        : <input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} style={inp} />}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,.55)", display: "flex", justifyContent: "center", padding: 20, overflow: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={{ background: dark ? "#14162a" : "#fff", color: T.text, borderRadius: 16, width: "100%", maxWidth: 1180, maxHeight: "94vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>👤 Генератор сторінки автора</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>лінк author.house формується одразу; «Зберегти» синкає дані на сайт</div>
          {pubUrl && <a href={pubUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", marginLeft: 8 }}>✓ збережено: {pubUrl}</a>}
          <button onClick={publish} disabled={publishing} style={{ marginLeft: "auto", background: publishing ? (T.bg || "#eee") : "linear-gradient(135deg,#6366f1,#a855f7)", color: publishing ? T.textMuted : "#fff", border: "none", borderRadius: 8, padding: "7px 16px", cursor: publishing ? "wait" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>{publishing ? "Зберігаю…" : (pubUrl ? "💾 Зберегти зміни" : "💾 Зберегти на author.house")}</button>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: T.textMuted, fontFamily: "inherit", fontWeight: 600 }}>✕ Закрити</button>
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* ── LEFT: форма ── */}
          <div style={{ width: 420, flexShrink: 0, overflowY: "auto", padding: 18, borderRight: `1px solid ${T.border}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Дані автора</div>
            {field("Ім'я та прізвище *", "name", "Іван Петренко")}
            {field("Посада / спеціалізація *", "jobTitle", "Лікар-ортопед")}
            {field("Досвід", "experience", "12 років практики")}
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Адреса профілю на author.house</label>
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.bg || "#fafafa", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 10px" }}>
                <span style={{ fontSize: 13, color: T.textMuted, whiteSpace: "nowrap" }}>author.house/</span>
                <input value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder={autoSlug || "slug"}
                  style={{ flex: 1, minWidth: 0, background: "none", color: T.text, border: "none", outline: "none", fontFamily: "inherit", fontSize: 13 }} />
              </div>
              <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 3, lineHeight: 1.4 }}>
                {profileUrl ? <>Лінк формується одразу: <a href={profileUrl} target="_blank" rel="noreferrer" style={{ color: "#6366f1", fontWeight: 600 }}>{profileUrl}</a>. Дані підуть на author.house лише коли натиснете «Зберегти». Slug авто з імені (можна змінити).</> : "Вкажіть ім'я — адреса профілю згенерується автоматично."}
              </div>
            </div>
            {field("Фото (URL)", "photo", "https://site.com/img/ivan.jpg")}
            {field("Короткий опис (під іменем)", "description", "Спеціаліст з ортопедичних устілок та захворювань стоп.", 2)}
            {field("Детальне біо", "bio", "Абзаци через порожній рядок…", 4)}
            {field("Освіта", "education", "НМУ ім. О.О. Богомольця")}
            {field("Регалії / сертифікати (по рядку)", "credentials", "Сертифікат ортопеда\nЧлен асоціації…", 3)}
            {field("Теми (через кому)", "knowsAbout", "ортопедія, устілки, плоскостопість")}
            {field("Місце роботи", "worksFor", proj?.name || "Orto-Line")}
            {field("Email (необов'язково)", "email", "doctor@site.com")}

            <div style={{ fontWeight: 700, fontSize: 13, margin: "14px 0 8px" }}>Профілі / джерела (sameAs)</div>
            {f.socials.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input value={s.url} onChange={(e) => setSocial(i, e.target.value)} placeholder="https://linkedin.com/in/…" style={inp} />
                <button onClick={() => rmSocial(i)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "0 10px", cursor: "pointer", color: T.textMuted }}>✕</button>
              </div>
            ))}
            <button onClick={addSocial} style={{ background: "none", border: `1px dashed ${T.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: T.textMuted, fontFamily: "inherit" }}>+ Додати профіль</button>

            {/* customization */}
            <div style={{ fontWeight: 700, fontSize: 13, margin: "18px 0 8px" }}>Вигляд</div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
                Колір <input type="color" value={opt.accent} onChange={(e) => setO("accent", e.target.value)} style={{ width: 34, height: 26, border: "none", background: "none", cursor: "pointer" }} />
              </label>
              <label style={{ fontSize: 12, color: T.textMuted }}>Тема
                <select value={opt.theme} onChange={(e) => setO("theme", e.target.value)} style={{ ...inp, width: "auto", marginLeft: 6, padding: "4px 8px" }}>
                  <option value="light">світла</option><option value="dark">темна</option>
                </select>
              </label>
              <label style={{ fontSize: 12, color: T.textMuted }}>Фото
                <select value={opt.avatar} onChange={(e) => setO("avatar", e.target.value)} style={{ ...inp, width: "auto", marginLeft: 6, padding: "4px 8px" }}>
                  <option value="circle">коло</option><option value="square">квадрат</option>
                </select>
              </label>
              <label style={{ fontSize: 12, color: T.textMuted }}>Макет
                <select value={opt.layout} onChange={(e) => setO("layout", e.target.value)} style={{ ...inp, width: "auto", marginLeft: 6, padding: "4px 8px" }}>
                  <option value="left">фото зліва</option><option value="center">по центру</option>
                </select>
              </label>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: T.textMuted }}>
              {[["showCreds", "Кваліфікація"], ["showTopics", "Теми"], ["showSocials", "Профілі"], ["showContact", "Контакт"]].map(([k, l]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="checkbox" checked={opt[k]} onChange={(e) => setO(k, e.target.checked)} /> {l}
                </label>
              ))}
            </div>
          </div>

          {/* ── RIGHT: прев'ю / код ── */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", gap: 8, padding: "10px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
              {[["preview", "👁 Прев'ю"], ["code", "</> HTML-код"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{ background: tab === k ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", color: tab === k ? "#fff" : T.textMuted, border: `1px solid ${tab === k ? "#6366f1" : T.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
              ))}
              {tab === "code" && (
                <button onClick={copy} style={{ marginLeft: "auto", background: copied ? "#16a34a" : "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{copied ? "✓ Скопійовано" : "⧉ Скопіювати код"}</button>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: dark ? "#0b0d17" : "#eef0f6" }}>
              {tab === "preview" ? (
                <iframe title="preview" srcDoc={previewDoc} style={{ width: "100%", height: "100%", minHeight: 520, border: "none", display: "block" }} />
              ) : (
                <textarea readOnly value={html} style={{ width: "100%", height: "100%", minHeight: 520, border: "none", padding: 16, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, lineHeight: 1.5, background: dark ? "#0b0d17" : "#fff", color: dark ? "#e8e9f3" : "#1a1a2e", resize: "none", boxSizing: "border-box" }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
